import React, { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { useAuth } from '../../contexts/AuthContext';
import { getPanelPalette, panelPalettes, getPanelBackground, panelBackgrounds, getPanelTextColor, panelTextColors } from '../../theme';

export default function PaletteDialog({ open, onClose, initialTab = 'palette' }) {
  const { currentUser, updatePanelPalette, updatePanelBackground, updatePanelTextColor } = useAuth();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(['palette', 'background', 'text'].includes(initialTab) ? initialTab : 'palette');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const sections = {
    palette: {
      title: 'Кнопки и акценты', options: panelPalettes, selected: getPanelPalette(currentUser?.panelPalette),
      update: updatePanelPalette, error: 'Не удалось сохранить палитру',
      description: 'Цвет кнопок, ссылок и выделений. Фон и цвет основного текста не изменятся.',
    },
    background: {
      title: 'Фон панели', options: panelBackgrounds, selected: getPanelBackground(currentUser?.panelBackground),
      update: updatePanelBackground, error: 'Не удалось сохранить фон',
      description: 'Фон рабочей области, меню, карточек и полей ввода. Цвет кнопок и текста выбирается отдельно.',
    },
    text: {
      title: 'Цвет текста', options: panelTextColors, selected: getPanelTextColor(currentUser?.panelTextColor),
      update: updatePanelTextColor, error: 'Не удалось сохранить цвет текста',
      description: 'Цвет основного текста, подписей и заголовков таблиц. Текст на цветных кнопках и цвета статусов сохраняют контраст.',
    },
  };
  const section = sections[activeTab];
  const isBackground = activeTab === 'background';
  const isText = activeTab === 'text';

  const changeTab = (event, tab) => {
    if (saving) return;
    setActiveTab(tab);
    setError('');
    setSaved(false);
  };

  const chooseColor = async id => {
    if (saving || section.selected.id === id) return;
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await section.update(id);
      setSaved(true);
    } catch (err) {
      setError(err.message || section.error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="appearance-title" aria-describedby="appearance-description">
      <DialogTitle id="appearance-title">Оформление</DialogTitle>
      <DialogContent>
        <Typography id="appearance-description" color="text.secondary" variant="body2" sx={{ mb: 2 }}>
          Настройки применяются сразу после сохранения и привязаны к вашему аккаунту на всех устройствах.
        </Typography>
        <Tabs value={activeTab} onChange={changeTab} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label="Настройки оформления" sx={{ mb: 2 }}>
          {Object.entries(sections).map(([id, item]) => (
            <Tab key={id} value={id} label={item.title} id={`appearance-tab-${id}`} aria-controls={`appearance-panel-${id}`} disabled={saving} />
          ))}
        </Tabs>
        <Box role="tabpanel" id={`appearance-panel-${activeTab}`} aria-labelledby={`appearance-tab-${activeTab}`} aria-busy={saving}>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>{section.description}</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box role="group" aria-label={section.title} sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(5, 1fr)' }, gap: 1.5 }}>
            {section.options.map((option, index) => {
              const highlight = activeTab === 'palette' ? option.main : theme.palette.primary.main;
              const selected = section.selected.id === option.id;
              const startsGroup = isBackground && (index === 0 || Boolean(option.vivid) !== Boolean(section.options[index - 1].vivid));
              return (
                <React.Fragment key={option.id}>
                  {startsGroup && <Typography component="h3" variant="subtitle2" sx={{ gridColumn: '1 / -1', mt: index === 0 ? 0 : 1 }}>{option.vivid ? 'Яркие' : 'Тёмные'}</Typography>}
                  <Button
                    variant="outlined"
                    aria-label={option.label}
                    aria-pressed={selected}
                    disabled={saving}
                    onClick={() => chooseColor(option.id)}
                    sx={{
                      position: 'relative', flexDirection: 'column', gap: 1, p: 1.5, minWidth: 0, fontSize: 12, color: 'text.primary',
                      borderColor: selected ? highlight : 'divider',
                      bgcolor: selected ? alpha(highlight, 0.1) : 'transparent',
                      '&:hover': { borderColor: highlight, bgcolor: alpha(highlight, 0.15) },
                      '&.Mui-focusVisible': { outline: `2px solid ${highlight}`, outlineOffset: 3 },
                    }}
                  >
                    <Box component="span" aria-hidden="true" sx={{
                      display: 'grid', placeItems: 'center', width: isBackground || isText ? 52 : 36, height: 36,
                      borderRadius: isBackground || isText ? '6px' : '50%', bgcolor: isText ? 'background.default' : option.main,
                      color: isText ? option.main : isBackground ? 'text.primary' : '#061b24', fontSize: 19, fontWeight: 700,
                      ...(isBackground || isText ? { border: '1px solid', borderColor: 'divider' } : {}),
                    }}>
                      {isText ? 'Aa' : selected && <CheckIcon fontSize="small" />}
                    </Box>
                    {isText && selected && <CheckIcon sx={{ position: 'absolute', top: 3, right: 3, fontSize: 14, color: 'primary.main' }} />}
                    {option.label}
                  </Button>
                </React.Fragment>
              );
            })}
          </Box>
          <Typography role="status" variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            {saving ? 'Сохранение…' : saved ? 'Настройка сохранена в аккаунте' : `Выбрано: ${section.selected.label}`}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={saving}>Готово</Button></DialogActions>
    </Dialog>
  );
}
